import { useState, useCallback, useRef } from 'react';
import { unifiedAIService } from '../services/unifiedAIService';
import type {
  ChatMessage,
  UnifiedChatRequest,
  Carrier,
  DocumentUploadProgress,
  AIAnalytics
} from '../types/ai-unified';

export interface UseUnifiedAIOptions {
  initialContextType?: 'general' | 'underwriting' | 'resources' | 'quotes';
  initialCarrierSlug?: string;
  autoDetectContext?: boolean;
  contextMetadata?: Record<string, any>; // Additional metadata to pass with messages
}

export interface UseUnifiedAIReturn {
  // State
  messages: ChatMessage[];
  sessionId: string | null;
  contextType: 'general' | 'underwriting' | 'resources' | 'quotes';
  carrierSlug: string | null;
  carriers: Carrier[];
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: DocumentUploadProgress | null;
  analytics: AIAnalytics | null;

  // Actions
  sendMessage: (message: string) => Promise<void>;
  setContextType: (type: 'general' | 'underwriting' | 'resources' | 'quotes') => void;
  setCarrierSlug: (slug: string | null) => void;
  uploadDocument: (file: File) => Promise<void>;
  clearMessages: () => void;
  addAssistantCardMessage: (message: string, cards: ChatMessage["cards"]) => void;
  loadCarriers: () => Promise<void>;
  loadAnalytics: () => Promise<void>;
  loadChatHistory: (sessionId: string) => Promise<void>;
  // Adopt a backend session id for a locally-started conversation (e.g. the
  // persisted quote-builder exchange) so follow-up messages append to it.
  adoptSession: (sessionId: string) => void;

  // Utilities
  isUnderwritingQuestion: (message: string) => boolean;
  extractCarrierFromMessage: (message: string) => string | null;
}

export const useUnifiedAI = (options: UseUnifiedAIOptions = {}): UseUnifiedAIReturn => {
  const {
    initialContextType = 'general',
    initialCarrierSlug = null,
    autoDetectContext = true,
    contextMetadata = {}
  } = options;

  // State
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [contextType, setContextType] = useState<'general' | 'underwriting' | 'resources' | 'quotes'>(initialContextType);
  const [carrierSlug, setCarrierSlug] = useState<string | null>(initialCarrierSlug);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<DocumentUploadProgress | null>(null);
  const [analytics, setAnalytics] = useState<AIAnalytics | null>(null);

  // Refs
  const messageIdCounter = useRef(0);

  // Send message with streaming support for live text effect
  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim() || isLoading) return;

    setIsLoading(true);

    // OPTIMISTIC UPDATE: Show user message immediately (before API call)
    const optimisticMessageId = `msg_${++messageIdCounter.current}`;
    const optimisticMessage: ChatMessage = {
      id: optimisticMessageId,
      message,
      response: '', // Will be filled as streaming chunks arrive
      timestamp: new Date(),
      source: 'general_ai',
      sessionId: sessionId || undefined
    };

    setMessages(prev => [...prev, optimisticMessage]);

    try {
      // Auto-detect context and carrier if enabled
      let detectedContextType = contextType;
      let detectedCarrierSlug = carrierSlug;

      if (autoDetectContext) {
        if (unifiedAIService.isUnderwritingQuestion(message)) {
          detectedContextType = 'underwriting';
          const extractedCarrier = unifiedAIService.extractCarrierFromMessage(message);
          if (extractedCarrier) {
            detectedCarrierSlug = extractedCarrier;
          }
        }
      }

      // Create request - backend will handle session creation if sessionId is missing
      const request: UnifiedChatRequest = {
        message,
        sessionId: sessionId || undefined, // Backend creates session if undefined
        contextType: detectedContextType,
        carrier_slug: detectedCarrierSlug || undefined,
        use_enhanced_underwriting: detectedContextType === 'underwriting',
        // Pass availableResources at top level for resource context
        available_resources: contextMetadata?.availableResources,
        metadata: {
          source: 'web_chat',
          category: detectedContextType === 'underwriting' ? 'underwriting_inquiry' : detectedContextType === 'resources' ? 'resources_inquiry' : 'general_inquiry',
          ...contextMetadata // Merge in any additional context metadata
        }
      };

      // Track streaming response
      let streamedResponse = '';

      // Try streaming first, fall back to regular if streaming fails
      try {
        const response = await unifiedAIService.sendMessageStream(
          request,
          // onChunk callback - update response character by character
          (chunk: string) => {
            streamedResponse += chunk;
            // Update the message with each new chunk (live typing effect)
            setMessages(prev => prev.map(msg =>
              msg.id === optimisticMessageId
                ? { ...msg, response: streamedResponse }
                : msg
            ));
          }
        );

        // Update session ID from final response
        if (response?.sessionId) {
          setSessionId(response.sessionId);
        }

        // Update context type if auto-detected
        if (autoDetectContext && detectedContextType !== contextType) {
          setContextType(detectedContextType);
        }

        // Update carrier if auto-detected
        if (autoDetectContext && detectedCarrierSlug && detectedCarrierSlug !== carrierSlug) {
          setCarrierSlug(detectedCarrierSlug);
        }

        // Final update with metadata from response
        setMessages(prev => prev.map(msg =>
          msg.id === optimisticMessageId
            ? {
                ...msg,
                response: streamedResponse || response?.response || 'I received your message but have no response to display.',
                source: response?.source || 'general_ai',
                confidence: response?.confidence,
                evidence_sources: response?.evidence_sources,
                suggested_actions: response?.suggested_actions,
                model_used: response?.model_used,
                sessionId: response?.sessionId || msg.sessionId,
                cards: (response as any)?.cards,
              }
            : msg
        ));
      } catch (streamError) {
        // Fall back to regular non-streaming API
        console.warn('Streaming failed, falling back to regular API:', streamError);

        const response = await unifiedAIService.sendMessage(request);

        if (response?.response) {
          if (response.sessionId) {
            setSessionId(response.sessionId);
          }

          if (autoDetectContext && detectedContextType !== contextType) {
            setContextType(detectedContextType);
          }

          if (autoDetectContext && detectedCarrierSlug && detectedCarrierSlug !== carrierSlug) {
            setCarrierSlug(detectedCarrierSlug);
          }

          setMessages(prev => prev.map(msg =>
            msg.id === optimisticMessageId
              ? {
                  ...msg,
                  response: response.response,
                  source: response.source || 'general_ai',
                  confidence: response.confidence,
                  evidence_sources: response.evidence_sources,
                  suggested_actions: response.suggested_actions,
                  model_used: response.model_used,
                  sessionId: response.sessionId || msg.sessionId,
                  cards: (response as any).cards,
                }
              : msg
          ));
        } else {
          setMessages(prev => prev.map(msg =>
            msg.id === optimisticMessageId
              ? { ...msg, response: 'Error: No response received from server. Please try again.' }
              : msg
          ));
        }
      }
    } catch (error) {
      // Update optimistic message with error
      setMessages(prev => prev.map(msg =>
        msg.id === optimisticMessageId
          ? { ...msg, response: 'Sorry, I encountered an error. Please try again.' }
          : msg
      ));
    } finally {
      setIsLoading(false);
    }
  }, [contextType, carrierSlug, sessionId, isLoading, autoDetectContext]);

  // Upload document
  const uploadDocument = useCallback(async (file: File) => {
    if (!carrierSlug) {
      throw new Error('Please select a carrier before uploading documents');
    }

    setIsUploading(true);
    setUploadProgress({
      fileId: `upload_${Date.now()}`,
      fileName: file.name,
      progress: 0,
      status: 'uploading'
    });

    try {
      const result = await unifiedAIService.uploadDocument(
        file,
        carrierSlug,
        (progress) => setUploadProgress(progress)
      );

      setUploadProgress({
        fileId: `upload_${Date.now()}`,
        fileName: file.name,
        progress: 100,
        status: 'completed',
        result
      });

      // Clear progress after 3 seconds
      setTimeout(() => {
        setUploadProgress(null);
      }, 3000);

    } catch (error) {

      setUploadProgress({
        fileId: `upload_${Date.now()}`,
        fileName: file.name,
        progress: 0,
        status: 'error',
        error: error instanceof Error ? error.message : 'Upload failed'
      });

      // Clear error after 5 seconds
      setTimeout(() => {
        setUploadProgress(null);
      }, 5000);
    } finally {
      setIsUploading(false);
    }
  }, [carrierSlug]);

  // Load carriers
  const loadCarriers = useCallback(async () => {
    try {
      const carriersData = await unifiedAIService.getCarriers();
      setCarriers(carriersData || []);

      if (!carriersData || carriersData.length === 0) {
        console.warn('[useUnifiedAI] No carriers available - underwriting features will be limited');
      }
    } catch (error) {
      console.error('[useUnifiedAI] Error loading carriers:', error);
      setCarriers([]); // Ensure carriers is always an array
    }
  }, []);

  // Load analytics
  const loadAnalytics = useCallback(async () => {
    try {
      const analyticsData = await unifiedAIService.getAnalytics();
      setAnalytics(analyticsData);
    } catch (error) {
    }
  }, []);

  // Load chat history
  const loadChatHistory = useCallback(async (sessionId: string) => {
    try {
      const history = await unifiedAIService.getChatHistory(sessionId);
      // Only update messages if history is valid (not undefined/null and is an array)
      if (history && Array.isArray(history)) {
        setMessages(history);
        setSessionId(sessionId);
      }
    } catch (error) {
    }
  }, []);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    setSessionId(null);
  }, []);

  /**
   * Inject a fully-formed assistant message into the chat WITHOUT calling
   * the backend AI. Used to surface UI-only cards (like the Techficient
   * quote form) in response to a deterministic action — the agent clicked
   * a button, we know exactly what to render, no LLM needed. Keeps the
   * chat surface uniform while sidestepping OpenAI quota / latency.
   */
  const adoptSession = useCallback((id: string) => {
    setSessionId(id);
  }, []);

  const addAssistantCardMessage = useCallback(
    (message: string, cards: ChatMessage["cards"]) => {
      const id = `local-${typeof crypto !== "undefined" && (crypto as any).randomUUID ? (crypto as any).randomUUID() : Date.now().toString(36)}`;
      const synthetic: ChatMessage = {
        id,
        message: "",
        response: message,
        timestamp: new Date(),
        source: "general_ai",
        cards,
      };
      setMessages((prev) => [...prev, synthetic]);
    },
    [],
  );

  // Utility functions
  const isUnderwritingQuestion = useCallback((message: string) => {
    return unifiedAIService.isUnderwritingQuestion(message);
  }, []);

  const extractCarrierFromMessage = useCallback((message: string) => {
    return unifiedAIService.extractCarrierFromMessage(message);
  }, []);

  return {
    // State
    messages,
    sessionId,
    contextType,
    carrierSlug,
    carriers,
    isLoading,
    isUploading,
    uploadProgress,
    analytics,

    // Actions
    sendMessage,
    setContextType,
    setCarrierSlug,
    uploadDocument,
    clearMessages,
    addAssistantCardMessage,
    loadCarriers,
    loadAnalytics,
    loadChatHistory,
    adoptSession,

    // Utilities
    isUnderwritingQuestion,
    extractCarrierFromMessage
  };
};
